import React from 'react';
import { FarmingPackage } from '@shared/types';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Package, DollarSign, ArrowRight, Check } from 'lucide-react';
import { useFarmStore } from '@/store/useFarmStore';
import { toast } from 'sonner';
import { useCurrency } from '@/hooks/use-currency';

export function PackageCard({ pkg }: { pkg: FarmingPackage }) {
  const addToCart = useFarmStore((s) => s.addToCart);
  const { formatFromBase } = useCurrency();

  const handleAdd = () => {
    addToCart(pkg.items);
    toast.success(`${pkg.name} added to cart`);
  };
  return (
    <Card className={`rounded-md border ${pkg.type === 'recommended' ? 'border-primary ring-1 ring-primary/10' : 'border-border'}`}>
      <CardHeader className="pb-4">
        <div className="flex justify-between items-start">
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{pkg.type}</div>
            <CardTitle className="text-lg font-bold">{pkg.name}</CardTitle>
          </div>
          {pkg.type === 'recommended' && (
            <Badge variant="default" className="bg-primary text-white text-[10px] uppercase rounded-sm">Recommended</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-between items-center border-b pb-4">
          <div>
            <div className="text-xl font-bold">{formatFromBase(pkg.totalCost)}</div>
            <div className="text-[10px] text-muted-foreground uppercase">Setup Investment</div>
          </div>
          <div className="text-right">
            <div className="text-sm font-bold text-green-600">+{pkg.roiPercentage}% ROI</div>
            <div className="text-[10px] text-muted-foreground uppercase">Estimated Margin</div>
            <div className="text-[10px] text-primary font-bold mt-1">Confidence {pkg.confidenceScore}%</div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="text-xs font-bold uppercase text-muted-foreground">Inclusions:</div>
          <ul className="space-y-1.5">
            {pkg.items.slice(0, 3).map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-xs">
                <Check className="h-3 w-3 text-primary" />
                <span className="text-muted-foreground">{item.quantity}x {item.product.name}</span>
              </li>
            ))}
            {pkg.items.length > 3 && (
              <li className="text-[10px] text-muted-foreground italic">+{pkg.items.length - 3} additional items</li>
            )}
          </ul>
        </div>
      </CardContent>
      <CardFooter>
        <Button size="sm" className="w-full rounded-md" onClick={handleAdd}>
          Select Package <ArrowRight className="ml-2 h-3 w-3" />
        </Button>
      </CardFooter>
    </Card>
  );
}